import api from './api';

const communicationsService = {
  // Get all communications with optional filters
  getCommunications: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.client_id) params.append('client_id', filters.client_id);
      if (filters.communication_type) params.append('communication_type', filters.communication_type);
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.follow_up_required) params.append('follow_up_required', filters.follow_up_required);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.search) params.append('search', filters.search);

      const queryString = params.toString();
      const url = queryString ? `/communications?${queryString}` : '/communications';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching communications:', error);
      throw error;
    }
  },

  // Get communication statistics for dashboard
  getCommunicationStats: async () => {
    try {
      const response = await api.get('/communications/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching communication statistics:', error);
      throw error;
    }
  },

  // Create a new communication record
  async createCommunication(communicationData) {
    try {
      const response = await api.post('/communications', communicationData);
      return response.data;
    } catch (error) {
      console.error('Error creating communication:', error);
      throw error;
    }
  },

  // Update an existing communication record
  async updateCommunication(id, updates) {
    try {
      const response = await api.put(`/communications/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating communication:', error);
      throw error;
    }
  },

  // Mark a communication's follow-up as complete
  async markFollowUpDone(id) {
    try {
      const response = await api.put(`/communications/${id}`, { follow_up_completed: true });
      return response.data;
    } catch (error) {
      console.error('Error marking follow-up done:', error);
      throw error;
    }
  },

  // Delete a communication record
  async deleteCommunication(id) {
    try {
      const response = await api.delete(`/communications/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting communication:', error);
      throw error;
    }
  },

  // Get communications for a specific client
  getClientCommunications: async (clientId) => {
    try {
      const response = await api.get(`/communications/client/${clientId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching client communications:', error);
      throw error;
    }
  },

  // Get follow-up reminders
  getFollowUpReminders: async () => {
    try {
      const response = await api.get('/communications/follow-ups');
      return response.data;
    } catch (error) {
      console.error('Error fetching follow-up reminders:', error);
      throw error;
    }
  },

  // Get communications by type
  getCommunicationsByType: async (type) => {
    try {
      const response = await api.get(`/communications?communication_type=${type}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching communications by type:', error);
      throw error;
    }
  },

  // Get communications by direction
  getCommunicationsByDirection: async (direction) => {
    try {
      const response = await api.get(`/communications?direction=${direction}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching communications by direction:', error);
      throw error;
    }
  },

  // Get communications requiring follow-up
  getFollowUpRequired: async () => {
    try {
      const response = await api.get('/communications?follow_up_required=true');
      return response.data;
    } catch (error) {
      console.error('Error fetching follow-up required communications:', error);
      throw error;
    }
  },

  // Get communications within date range
  getCommunicationsByDateRange: async (dateFrom, dateTo) => {
    try {
      const response = await api.get(`/communications?date_from=${dateFrom}&date_to=${dateTo}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching communications by date range:', error);
      throw error;
    }
  },

  // Get quote messages for a specific quote
  getQuoteMessages: async (quoteId) => {
    try {
      const response = await api.get(`/communications/quote-messages/${quoteId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching quote messages:', error);
      throw error;
    }
  },

  // Reply to a quote message
  replyToQuoteMessage: async (quoteId, message) => {
    try {
      const response = await api.post(`/communications/quote-messages/${quoteId}/reply`, { message });
      return response.data;
    } catch (error) {
      console.error('Error sending quote message reply:', error);
      throw error;
    }
  },

  // Get itinerary messages for a specific itinerary
  getItineraryMessages: async (itineraryId) => {
    try {
      const response = await api.get(`/communications/itinerary-messages/${itineraryId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching itinerary messages:', error);
      throw error;
    }
  },

  // Reply to an itinerary message
  replyToItineraryMessage: async (itineraryId, message) => {
    try {
      const response = await api.post(`/communications/itinerary-messages/${itineraryId}/reply`, { message });
      return response.data;
    } catch (error) {
      console.error('Error sending itinerary message reply:', error);
      throw error;
    }
  },

  // Migrate existing quote messages to communications table
  migrateQuoteMessages: async () => {
    try {
      const response = await api.post('/communications/migrate-quote-messages');
      return response.data;
    } catch (error) {
      console.error('Error migrating quote messages:', error);
      throw error;
    }
  }
};

export default communicationsService;